import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ChangeEventHandler } from 'react';
import { useRef } from 'react';
import toast from 'react-hot-toast';
import {
  importLabProtocolService,
  LabProtocolImportApiError,
  LabProtocolImportValidationError,
} from '@siloreal:shared:api/service/laboratory/lab-protocol-import';
import * as HOOKS from '@siloreal:shared:hooks';

const EXCEL_FILE_EXTENSION = '.xlsx';
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPTED_EXCEL_MIME_TYPES = new Set([EXCEL_MIME_TYPE, 'application/octet-stream', 'application/zip']);
const SILOBAG_LIST_QUERY_KEY = ['silopro-vrr-list'] as const;

function validateExcelFile(file: File): string | null {
  const hasExcelExtension = file.name.toLowerCase().endsWith(EXCEL_FILE_EXTENSION);
  const hasAcceptedMimeType = file.type === '' || ACCEPTED_EXCEL_MIME_TYPES.has(file.type);
  const i18n = HOOKS.useLocale.getState();

  if (!hasExcelExtension || !hasAcceptedMimeType) {
    return `${i18n.t('temp.common.error.select_an_excel_file')} .xlsx.`;
  }

  if (file.size === 0) {
    return i18n.t('temp.common.error.empty_file');
  }

  return null;
}

function getErrorFileName(fileName: string): string {
  const baseName = fileName.replace(/\.xlsx$/i, '');

  return `${baseName} - ERROR.xlsx`;
}

function getImportErrorMessage(error: unknown): string {
  const i18n = HOOKS.useLocale.getState();

  const IMPORT_ERROR_MESSAGES: Record<number, string> = {
    10019: `${i18n.t('temp.common.error.spreadsheet_already_imported')} (E.#10019)`,
    10020: `${i18n.t('temp.common.error.spreadsheet_not_for_selected_silobag')} (E.#10020)`,
  };

  if (error instanceof LabProtocolImportApiError) {
    return (error.code && IMPORT_ERROR_MESSAGES[error.code]) || error.message;
  }

  return i18n.t('temp.common.error.quality_spreadsheet_download');
}

export function useQualitySampleUpload() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useMutation({ mutationFn: importLabProtocolService });

  const openFilePicker = (): void => {
    if (!importMutation.isPending) fileInputRef.current?.click();
  };

  const uploadQualitySamples: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file) return;

    const validationError = validateExcelFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const i18n = HOOKS.useLocale.getState();
    const loadingToastId = toast.loading(
      i18n.t('temp.common.toasts.loading.importing_quality_sample_spreadsheet')
    );

    try {
      await importMutation.mutateAsync({ file });
      await queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });
      toast.success(i18n.t('temp.common.toasts.success.spreadsheet_imported'), { id: loadingToastId });
    } catch (error) {
      if (error instanceof LabProtocolImportValidationError) {
        try {
          const { downloadExcelBlob } = await import('@siloreal:shared:sdk/excel/excel-generator');

          downloadExcelBlob(error.file, getErrorFileName(file.name));
          toast.error(i18n.t('temp.common.toasts.error.spreadsheet_downloaded_to_fix'), {
            id: loadingToastId,
          });
        } catch {
          toast.error(i18n.t('temp.common.toasts.error.spreadsheet_download_failed'), {
            id: loadingToastId,
          });
        }
        return;
      }

      toast.error(getImportErrorMessage(error), { id: loadingToastId });
    }
  };

  return {
    fileInputRef,
    inputAccept: `${EXCEL_FILE_EXTENSION},${EXCEL_MIME_TYPE}`,
    isUploading: importMutation.isPending,
    openFilePicker,
    uploadQualitySamples,
  };
}
